import { cn } from '@/lib/utils';
import type { BannerColor } from '@cotality/marketplace-contracts/endpoints/banner';
import { cva } from 'class-variance-authority';

/**
 * Presentational sidebar banner — the SINGLE source of banner rendering,
 * shared by `<SidebarBanner/>` (live data) and the admin tab's live preview so
 * the two can never drift. Colour variants map to the semantic banner tokens
 * (see styles/globals.css) so contrast + dark mode are correct.
 */
const bannerVariants = cva('rounded-md border px-3 py-2 text-left', {
  variants: {
    color: {
      info: 'bg-banner-info text-banner-info-foreground border-banner-info-border',
      warning: 'bg-banner-warning text-banner-warning-foreground border-banner-warning-border',
      success: 'bg-banner-success text-banner-success-foreground border-banner-success-border',
      error: 'bg-banner-error text-banner-error-foreground border-banner-error-border',
      neutral: 'bg-banner-neutral text-banner-neutral-foreground border-banner-neutral-border',
    },
  },
  defaultVariants: { color: 'info' },
});

export interface BannerCardProps {
  title: string;
  description: string;
  color: BannerColor;
  className?: string;
}

export function BannerCard({ title, description, color, className }: BannerCardProps) {
  return (
    <output
      aria-live="polite"
      className={cn(
        bannerVariants({ color }),
        'block group-data-[collapsible=icon]:hidden',
        className,
      )}
    >
      <p className="text-sm font-semibold leading-tight break-words">{title}</p>
      {description ? (
        <p className="mt-0.5 max-h-40 overflow-y-auto overscroll-contain whitespace-pre-line break-words pr-1 text-xs leading-snug opacity-90">
          {description}
        </p>
      ) : null}
    </output>
  );
}
